import { toast } from "@/client/hooks/use-toast";
import { Translate } from "@/components/common/Translate/Translate";
import { getQueryClient } from "@/lib/queryClient";
import { formatBytes } from "@/utils/formatBytes";
import { translateFunc } from "@/utils/translateFunc";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { startTransition, useEffect, useState } from "react";
import translatedRoutesSlugs from "translatedRouteSlugs";
import { Result } from "types/result";
import { consumeQuotaOptions } from "../queries/consumeQuota";
import { getUsageDetailOptions } from "../queries/getUsageDetails";
import { UsageDetail } from "../types/UsageDetail";
import Link from "next/link";
import { useTranslation } from "next-i18next";

const USER_TYPE_KEY = "ir-user-type";

export type Quotamanager = ReturnType<typeof useDailyQuota> & {
    deleteQuotaExceededImages?: boolean;
};

export const useDailyQuota = () => {
    const queryClient = getQueryClient();
    const { data, isFetching, isLoading } = useQuery(getUsageDetailOptions());
    const consumeQuotaMutation = useMutation(consumeQuotaOptions());
    const usageDetail = data?.data || null;
    const [userType, setUserType] = useState("free");
    const router = useRouter();
    const { t } = useTranslation();

    const ensureUsageDetail = async () => {
        return await queryClient.ensureQueryData(getUsageDetailOptions());
    };

    const invalidateDailyUsageQuery = () => {
        queryClient.invalidateQueries(getUsageDetailOptions());
    };

    const getFreshUsageData = async (): Promise<UsageDetail> => {
        const query = await ensureUsageDetail();
        let { data } = query;
        if (!data) throw new Error("Failed to fetch daily quota information");
        const hasCacheTimeExceeded =
            (new Date().getTime() - data.fetchedAt) / 1000 >
            (+(process.env.NEXT_PUBLIC_DAILY_QUOTA_DATA_CACHE_TIME || "") || 30); // wait 30s
        if (
            queryClient.getQueryState(getUsageDetailOptions().queryKey)?.fetchStatus !== "fetching" &&
            data.fetchedAt &&
            hasCacheTimeExceeded
        ) {
            await queryClient.refetchQueries(getUsageDetailOptions());
            const newData = await ensureUsageDetail();
            if (newData.data) {
                data = newData.data;
            }
        }
        return data;
    };

    const getRemainingQuota = async () => {
        const data = await getFreshUsageData();
        return data.limit === null ? Infinity : Math.max(data.limit - data.usage, 0);
    };

    const getRemainingQuotaSynchronously = () => {
        const data = queryClient.getQueryData(getUsageDetailOptions().queryKey);
        if (!data || !data.data || !!data.error) throw new Error("Usage detail not fetched yet");
        const { limit, usage } = data.data;
        return limit === null ? Infinity : Math.max(limit - usage, 0);
    };

    const consumeQuota = (count: number) => {
        queryClient.setQueryData(getUsageDetailOptions().queryKey, (data) => {
            const newData = structuredClone(data);
            if (!newData?.data) return newData;
            newData.data.usage += count;
            return newData;
        });
    };

    const consumeQuotaByFrontendTool = () => {
        consumeQuota(1);
        consumeQuotaMutation.mutate(undefined, {
            onSuccess: (newData) => {
                queryClient.setQueryData(getUsageDetailOptions().queryKey, (data) => {
                    const structuredData = structuredClone(data);
                    if (!structuredData?.data) return structuredData;
                    structuredData.data = (newData as Result<UsageDetail, Error>).data ?? structuredData.data;
                    return structuredData;
                });
            },
        });
    };

    const handleUserType = () => {
        if (!usageDetail) {
            const userType = localStorage.getItem(USER_TYPE_KEY);
            if (userType) startTransition(() => setUserType(userType));
            return;
        }
        startTransition(() => setUserType(usageDetail.userType));
        localStorage.setItem(USER_TYPE_KEY, usageDetail.userType);
    };

    const maxFileSizeErrorToastTitle = translateFunc("common:max-file-size-error-title");
    const maxFileSizeErrorToastDescription = `${translateFunc("common:max-file-size-error-description", {
        size: usageDetail ? formatBytes(usageDetail.fileSizeLimit) : "10 MB",
    })}`;
    const UpgradeToConvertLargerFiles = () => {
        // not getting the data from state to avoid stale state issues
        const userType = localStorage.getItem(USER_TYPE_KEY);
        if (userType === "ultra") return null;
        return (
            <Translate
                keyName="common:upgrade-to-convert-larger-files"
                overrideValue={pleaseUpgradeText}
                linkRenderer={(label) => (
                    <Link href={pricingLink} className="underline" onClick={(e) => e.stopPropagation()}>
                        {label}
                    </Link>
                )}
            />
        );
    };
    const showMaxFileSizeErrorToast = () => {
        toast({
            title: maxFileSizeErrorToastTitle,
            description: (
                <>
                    {maxFileSizeErrorToastDescription}
                    <UpgradeToConvertLargerFiles />
                </>
            ),
            variant: "destructive",
            duration: Infinity,
        });
    };

    const getBatchLimit = async () => {
        const { data } = await ensureUsageDetail();
        if (data && data.userType === "ultra") {
            return 100;
        } else if (data && data.userType === "pro") {
            return 20;
        } else {
            return 5;
        }
    };

    const getBatchLimitSynchronously = () => {
        const data = queryClient.getQueryData(getUsageDetailOptions().queryKey);
        if (!data || !data.data || !!data.error) throw new Error("Usage detail not fetched yet");
        const userType = data.data.userType;
        if (userType === "ultra") {
            return 100;
        } else if (userType === "pro") {
            return 20;
        } else {
            return 5;
        }
    };

    const pricingLink =
        router.locale === "en"
            ? "/pricing"
            : `/${router.locale}/${translatedRoutesSlugs["/pricing"][router.locale || "en"]}`;
    const batchLimitErrorTitle = translateFunc("common:batch-limit-exceeded-title");
    const batchLimitErrorText = translateFunc("common:batch-limit-exceeded-msg", { size: "--" });
    const pleaseUpgradeText = t("common:please-upgrade");
    const PleaseUpgrade = () => {
        // not getting the data from state to avoid stale state issues
        const userType = localStorage.getItem(USER_TYPE_KEY);
        if (userType === "ultra") return null;
        return (
            <Translate
                keyName="common:please-upgrade"
                overrideValue={pleaseUpgradeText}
                linkRenderer={(label) => (
                    <Link href={pricingLink} className="underline" onClick={(e) => e.stopPropagation()}>
                        {label}
                    </Link>
                )}
            />
        );
    };
    const showBatchLimitErrorToast = async () => {
        const batchLimit = await getBatchLimit();
        toast({
            title: batchLimitErrorTitle,
            description: (
                <div>
                    {batchLimitErrorText.replace("--", `${batchLimit}`)}
                    <PleaseUpgrade />
                </div>
            ),
            variant: "destructive",
            duration: Infinity,
        });
    };

    const quotaLimitExceededTitle = translateFunc("common:quota-limit-exceeded-toast-title");
    const quotaLimitExceededDescription = translateFunc("common:quota-limit-exceeded-toast-description");
    const showQuotaLimitExceededToast = () => {
        toast({
            title: quotaLimitExceededTitle,
            description: (
                <div>
                    {quotaLimitExceededDescription} <PleaseUpgrade />
                </div>
            ),
            variant: "destructive",
            duration: Infinity,
        });
    };

    const showQuotaLimitExceededDeletedImagesToastDescription = translateFunc(
        "common:quota-limit-exceeded-deleted-images-toast-description",
    );
    const upgradeToContinueText = t("common:upgrade-to-continue");

    const UpgradeToContinue = () => {
        // not getting the data from state to avoid stale state issues
        const userType = localStorage.getItem(USER_TYPE_KEY);
        if (userType === "ultra") return null;
        return (
            <Translate
                keyName="common:upgrade-to-continue"
                overrideValue={upgradeToContinueText}
                linkRenderer={(label) => (
                    <Link href={pricingLink} className="underline" onClick={(e) => e.stopPropagation()}>
                        {label}
                    </Link>
                )}
            />
        );
    };
    const showQuotaLimitExceededDeletedImagesToast = () => {
        toast({
            title: quotaLimitExceededTitle,
            description: (
                <div>
                    {showQuotaLimitExceededDeletedImagesToastDescription} <UpgradeToContinue />
                </div>
            ),
            variant: "destructive",
            duration: Infinity,
        });
    };

    useEffect(handleUserType, [usageDetail?.userType]);

    return {
        userType,
        usageDetail,
        getRemainingQuota,
        ensureUsageDetail,
        isFetching,
        isLoading,
        invalidateDailyUsageQuery,
        consumeQuota,
        consumeQuotaByFrontendTool,
        showMaxFileSizeErrorToast,
        getBatchLimit,
        showBatchLimitErrorToast,
        showQuotaLimitExceededToast,
        showQuotaLimitExceededDeletedImagesToast,
        getRemainingQuotaSynchronously,
        getBatchLimitSynchronously,
    };
};
